import React, { useState, useEffect, useCallback } from 'react';
import './TabVacunas.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';

const BASE = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.VACUNAS;

const EMPTY = {
  nombreVacuna: '', laboratorio: '', lote: '',
  fechaAplicacion: new Date().toISOString().split('T')[0],
  fechaProximaAplicacion: '', dosis: '', veterinario: '', observaciones: ''
};

export default function TabVacunas({ animalId }) {
  const [vacunas, setVacunas]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando]     = useState(null);
  const [form, setForm]             = useState(EMPTY);
  const [guardando, setGuardando]   = useState(false);
  const [error, setError]           = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${BASE}/animal/${animalId}`);
      setVacunas(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNueva = () => {
    setEditando(null);
    setForm(EMPTY);
    setError('');
    setMostrarForm(true);
  };

  const abrirEditar = (v) => {
    setEditando(v.id);
    setForm({
      nombreVacuna:          v.nombreVacuna          || '',
      laboratorio:           v.laboratorio           || '',
      lote:                  v.lote                  || '',
      fechaAplicacion:       v.fechaAplicacion       || '',
      fechaProximaAplicacion:v.fechaProximaAplicacion || '',
      dosis:                 v.dosis                 || '',
      veterinario:           v.veterinario           || '',
      observaciones:         v.observaciones         || '',
    });
    setMostrarForm(true);
    setError('');
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.nombreVacuna.trim())    return setError('El nombre de la vacuna es obligatorio.');
    if (!form.fechaAplicacion)        return setError('La fecha de aplicación es obligatoria.');

    setGuardando(true); setError('');
    try {
      const payload = { ...form, animalId: parseInt(animalId) };
      const url    = editando ? `${BASE}/${editando}` : BASE;
      const method = editando ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Error al guardar');
      }
      await cargar();
      setMostrarForm(false);
      setEditando(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta vacuna?')) return;
    try {
      await apiFetch(`${BASE}/${id}`, { method: 'DELETE' });
      await cargar();
    } catch (e) {
      alert('Error al eliminar');
    }
  };

  const formatFecha = (f) => f ? new Date(f + 'T00:00:00').toLocaleDateString('es-PY') : '-';

  const getBadgeVencimiento = (v) => {
    if (!v.fechaProximaAplicacion) return null;
    if (v.vencida) return <span className="badge badge-vencida">Vencida</span>;
    if (v.diasParaProxima <= 30)   return <span className="badge badge-proxima">En {v.diasParaProxima}d</span>;
    return <span className="badge badge-ok">OK</span>;
  };

  return (
    <div className="tab-vacunas">
      <div className="tab-toolbar">
        <h3>💉 Historial de Vacunaciones</h3>
        <button className="btn-primary btn-sm" onClick={abrirNueva}>+ Registrar Vacuna</button>
      </div>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : vacunas.length === 0 ? (
        <div className="empty-state">
          <p>💉 No hay vacunas registradas para este animal.</p>
          <button className="btn-primary btn-sm" onClick={abrirNueva}>Registrar primera vacuna</button>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Vacuna</th>
              <th>Laboratorio</th>
              <th>Lote</th>
              <th>Fecha Aplicación</th>
              <th>Próxima Aplicación</th>
              <th>Dosis</th>
              <th>Veterinario</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {vacunas.map(v => (
              <tr key={v.id}>
                <td><strong>{v.nombreVacuna}</strong></td>
                <td>{v.laboratorio || '-'}</td>
                <td>{v.lote || '-'}</td>
                <td>{formatFecha(v.fechaAplicacion)}</td>
                <td>{formatFecha(v.fechaProximaAplicacion)}</td>
                <td>{v.dosis || '-'}</td>
                <td>{v.veterinario || '-'}</td>
                <td>{getBadgeVencimiento(v)}</td>
                <td>
                  <div className="acciones">
                    <button className="btn-info btn-sm" onClick={() => abrirEditar(v)}>✏️</button>
                    <button className="btn-danger btn-sm" onClick={() => eliminar(v.id)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Formulario */}
      {mostrarForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setMostrarForm(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editando ? 'Editar Vacuna' : 'Registrar Vacuna'}</h2>
              <button className="modal-close" onClick={() => setMostrarForm(false)}>✕</button>
            </div>
            <form onSubmit={guardar}>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-grid">
                <div className="form-group full">
                  <label>Nombre de la Vacuna *</label>
                  <input name="nombreVacuna" value={form.nombreVacuna}
                         onChange={e => setForm(p => ({...p, nombreVacuna: e.target.value}))}
                         placeholder="Ej: Aftosa, Brucelosis, Rabia" required />
                </div>
                <div className="form-group">
                  <label>Laboratorio</label>
                  <input name="laboratorio" value={form.laboratorio}
                         onChange={e => setForm(p => ({...p, laboratorio: e.target.value}))}
                         placeholder="Laboratorio fabricante" />
                </div>
                <div className="form-group">
                  <label>Lote</label>
                  <input name="lote" value={form.lote}
                         onChange={e => setForm(p => ({...p, lote: e.target.value}))}
                         placeholder="Número de lote" />
                </div>
                <div className="form-group">
                  <label>Fecha Aplicación *</label>
                  <input type="date" value={form.fechaAplicacion}
                         onChange={e => setForm(p => ({...p, fechaAplicacion: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label>Próxima Aplicación</label>
                  <input type="date" value={form.fechaProximaAplicacion}
                         onChange={e => setForm(p => ({...p, fechaProximaAplicacion: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label>Dosis</label>
                  <input value={form.dosis}
                         onChange={e => setForm(p => ({...p, dosis: e.target.value}))}
                         placeholder="Ej: 2ml" />
                </div>
                <div className="form-group">
                  <label>Veterinario</label>
                  <input value={form.veterinario}
                         onChange={e => setForm(p => ({...p, veterinario: e.target.value}))}
                         placeholder="Nombre del veterinario" />
                </div>
                <div className="form-group full">
                  <label>Observaciones</label>
                  <textarea value={form.observaciones}
                            onChange={e => setForm(p => ({...p, observaciones: e.target.value}))}
                            rows={2} placeholder="Notas adicionales..." />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarForm(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : '✓ Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
